import { ApiError, apiRequest, refreshSession } from '../api/client';
import { useSessionStore } from '../auth/session-store';

export const DELIVERY_NOTE_STATUSES = [
  'PENDING',
  'DISPATCHED',
  'DELIVERED',
  'SIGNED',
  'INVOICED',
  'VOID',
] as const;
export type DeliveryNoteStatus = (typeof DELIVERY_NOTE_STATUSES)[number];

/** Matches `apps/api` `StoredDeliveryNoteItem` (`delivery-note.repository.ts`) — never `unitPriceCents`/`vatRate` (audit-only, never exposed on reads or the PDF). */
export interface DeliveryNoteItem {
  id: string;
  menuId: string;
  menuNameSnapshot: string;
  orderedQuantity: number;
  deliveredQuantity: number;
}

/** Matches `apps/api` `StoredDeliveryNoteSignature`. */
export interface DeliveryNoteSignature {
  id: string;
  receiverName: string;
  receiverDocument: string | null;
  receiverRole: string | null;
  signedAt: string;
  serverReceivedAt: string;
  needsReview: boolean;
}

/** Matches `apps/api` `StoredDeliveryNote` (`delivery-note.repository.ts`). */
export interface DeliveryNote {
  id: string;
  noteNumber: string;
  orderId: string;
  customerId: string;
  siteId: string;
  serviceDate: string;
  status: DeliveryNoteStatus;
  revision: number;
  customerNameSnapshot: string;
  customerTaxIdSnapshot: string;
  siteNameSnapshot: string;
  siteAddressSnapshot: unknown;
  /** Absent or null on notes from before the points of sale; the name is frozen at issue. */
  operatingUnitId?: string | null;
  operatingUnitNameSnapshot?: string | null;
  issuedAt: string;
  dispatchedAt: string | null;
  deliveredAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  items: DeliveryNoteItem[];
  signature: DeliveryNoteSignature | null;
  createdAt: string;
  updatedAt: string;
  /** Only present on `GET /delivery-notes/:id` (detail), never on the list — a live read of `CustomerSite.defaultReceiverName`, used to pre-fill the on-screen signature form. */
  defaultReceiverName?: string | null;
}

export interface DeliveryNoteSearchParams {
  serviceDateFrom?: string | undefined;
  serviceDateTo?: string | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  operatingUnitId?: string | undefined;
  status?: DeliveryNoteStatus | undefined;
  page: number;
  pageSize: number;
}

export interface DeliveryNoteSearchResult {
  items: DeliveryNote[];
  page: number;
  pageSize: number;
  total: number;
}

/** The largest page `GET /delivery-notes` accepts; anything above is a 400 (`delivery-note.dto.ts`'s `listDeliveryNotesQuerySchema`). */
export const MAX_DELIVERY_NOTE_PAGE_SIZE = 100;

/** Builds the shared querystring for `GET /delivery-notes` and `POST /delivery-notes/print-batch`, so list filters and the batch print never drift apart on how each param is serialized. */
function buildDeliveryNoteQuery(
  params: Omit<DeliveryNoteSearchParams, 'page' | 'pageSize'>,
): URLSearchParams {
  const query = new URLSearchParams();

  if (params.serviceDateFrom !== undefined) {
    query.set('serviceDateFrom', params.serviceDateFrom);
  }
  if (params.serviceDateTo !== undefined) {
    query.set('serviceDateTo', params.serviceDateTo);
  }
  if (params.customerId !== undefined) {
    query.set('customerId', params.customerId);
  }
  if (params.siteId !== undefined) {
    query.set('siteId', params.siteId);
  }
  if (params.operatingUnitId !== undefined) {
    query.set('operatingUnitId', params.operatingUnitId);
  }
  if (params.status !== undefined) {
    query.set('status', params.status);
  }

  return query;
}

/** Matches `apps/api` `GET /delivery-notes` (`listDeliveryNotesQuerySchema`). */
export function listDeliveryNotes(
  params: DeliveryNoteSearchParams,
): Promise<DeliveryNoteSearchResult> {
  const query = buildDeliveryNoteQuery(params);
  query.set('page', String(params.page));
  query.set('pageSize', String(Math.min(params.pageSize, MAX_DELIVERY_NOTE_PAGE_SIZE)));

  return apiRequest<DeliveryNoteSearchResult>(`/delivery-notes?${query.toString()}`);
}

/** Matches `apps/api` `GET /delivery-notes/:id`. */
export function getDeliveryNote(id: string): Promise<DeliveryNote> {
  return apiRequest<DeliveryNote>(`/delivery-notes/${id}`);
}

export interface GenerateDeliveryNotesResult {
  created: string[];
}

/** Matches `apps/api` `POST /delivery-notes/generate`. */
export function generateDeliveryNotes(serviceDate: string): Promise<GenerateDeliveryNotesResult> {
  return apiRequest<GenerateDeliveryNotesResult>('/delivery-notes/generate', {
    method: 'POST',
    body: JSON.stringify({ serviceDate }),
  });
}

/** Matches `apps/api` `POST /delivery-notes/:id/dispatch`. */
export function dispatchDeliveryNote(id: string): Promise<DeliveryNote> {
  return apiRequest<DeliveryNote>(`/delivery-notes/${id}/dispatch`, { method: 'POST' });
}

/** Matches `apps/api` `POST /delivery-notes/:id/deliver`. */
export function markDeliveryNoteDelivered(id: string): Promise<DeliveryNote> {
  return apiRequest<DeliveryNote>(`/delivery-notes/${id}/deliver`, { method: 'POST' });
}

/** Matches `apps/api` `POST /delivery-notes/:id/void`. */
export function voidDeliveryNote(id: string, voidReason: string): Promise<DeliveryNote> {
  return apiRequest<DeliveryNote>(`/delivery-notes/${id}/void`, {
    method: 'POST',
    body: JSON.stringify({ voidReason }),
  });
}

export interface RequestScannedSignatureUploadResult {
  uploadUrl: string;
  key: string;
  expiresAt: string;
}

/** Matches `apps/api` `POST /delivery-notes/:id/upload-signed/request-url`. */
export function requestScannedSignatureUpload(
  id: string,
  contentType: string,
  sizeBytes: number,
): Promise<RequestScannedSignatureUploadResult> {
  return apiRequest<RequestScannedSignatureUploadResult>(
    `/delivery-notes/${id}/upload-signed/request-url`,
    { method: 'POST', body: JSON.stringify({ contentType, sizeBytes }) },
  );
}

export interface ConfirmScannedSignatureInput {
  key: string;
  receiverName: string;
  receiverDocument?: string | undefined;
  receiverRole?: string | undefined;
  signedAt?: string | undefined;
}

/** Matches `apps/api` `POST /delivery-notes/:id/upload-signed` (confirmation). */
export function confirmScannedSignature(
  id: string,
  input: ConfirmScannedSignatureInput,
): Promise<DeliveryNote> {
  return apiRequest<DeliveryNote>(`/delivery-notes/${id}/upload-signed`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Direct `PUT` to the presigned URL, same criterion as `contract-document-tab.tsx`'s
 * `uploadToPresignedUrl`: `XMLHttpRequest` rather than `fetch`, since `fetch` exposes
 * no upload progress event.
 */
/** The storage refused or never received the file (not a problem with the user's connection to the API). */
export class UploadFailedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadFailedError';
  }
}

export function uploadScannedSignatureFile(
  uploadUrl: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new UploadFailedError(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new UploadFailedError('Upload failed'));
    xhr.send(file);
  });
}

function sendBlobRequest(path: string, init: RequestInit): Promise<Response> {
  const token = useSessionStore.getState().session?.accessToken;
  const headers = new Headers(init.headers);

  if (token !== undefined) {
    headers.set('authorization', `Bearer ${token}`);
  }
  if (init.body !== undefined && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  return fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' });
}

/** Keeps the server's `code` so the page can explain the refusal. */
async function toPdfApiError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { message?: string };

    return new ApiError(response.status, body.message ?? response.statusText, body);
  } catch {
    return new ApiError(response.status, response.statusText);
  }
}

/**
 * Triggers a browser download for a PDF response, same single-flight
 * 401-refresh-and-retry criterion `orders/api.ts`'s `exportOrders` already
 * applies: a bare `fetch()` with no refresh would leave a concurrently
 * rotated access token as an unrecovered 401 and the download would never
 * start.
 */
async function downloadPdf(path: string, init: RequestInit, filename: string): Promise<void> {
  let response = await sendBlobRequest(path, init);

  if (response.status === 401) {
    const renewed = await refreshSession();

    if (renewed) {
      response = await sendBlobRequest(path, init);
    }
  }

  if (!response.ok) {
    throw await toPdfApiError(response);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();

  // Deferring the revoke lets the download begin first, same reasoning as
  // `exportOrders`'s own `setTimeout` — revoking in the same tick raced the
  // browser reading the blob URL under load.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Matches `apps/api` `GET /delivery-notes/:id/pdf`. */
export function downloadDeliveryNotePdf(id: string, noteNumber: string): Promise<void> {
  return downloadPdf(`/delivery-notes/${id}/pdf`, { method: 'GET' }, `remito-${noteNumber}.pdf`);
}

/** Matches `apps/api` `POST /delivery-notes/print-batch`: a single multi-page PDF with every remito pending dispatch for the date, never a ZIP. */
export function printDeliveryNotesBatch(serviceDate: string, operatingUnitId?: string): Promise<void> {
  return downloadPdf(
    '/delivery-notes/print-batch',
    {
      method: 'POST',
      body: JSON.stringify({
        serviceDate,
        ...(operatingUnitId === undefined || operatingUnitId === '' ? {} : { operatingUnitId }),
      }),
    },
    `remitos-${serviceDate}.pdf`,
  );
}

export interface RequestSignedScanDownloadUrlResult {
  downloadUrl: string;
  expiresAt: string;
}

/** Matches `apps/api` `GET /delivery-notes/:id/signed-scan`: a short-lived presigned URL, never the raw S3 key. Serves either the scanned upload or the persisted signed PDF — the caller never needs to know which. */
export function requestSignedScanDownloadUrl(
  id: string,
): Promise<RequestSignedScanDownloadUrlResult> {
  return apiRequest<RequestSignedScanDownloadUrlResult>(`/delivery-notes/${id}/signed-scan`);
}

export interface SignOnScreenInput {
  receiverName: string;
  receiverDocument: string;
  receiverRole?: string | undefined;
  /** `data:image/png;base64,...` exported from `SignaturePad`. */
  signatureDataUrl: string;
}

/** Matches `apps/api` `POST /delivery-notes/:id/sign`. */
export function signOnScreen(id: string, input: SignOnScreenInput): Promise<DeliveryNote> {
  return apiRequest<DeliveryNote>(`/delivery-notes/${id}/sign`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
